import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import { Input } from "@shadcn-in-nx/ui/input";
import { Search, Code, ChevronLeft, Loader2, AppWindow } from "lucide-react";
import { useBluelagoon } from "@bluelagoon";
import { useDebounce } from "../hooks/useDebounce";
import { toast } from "sonner";
import { buildSharedFunctionName } from "@utils";

export function FunctionSearchModal({ open, onOpenChange, onSelect, workflowId, workflowType }) {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearch = useDebounce(searchTerm, 300);
    const [apps, setApps] = useState([]);
    const [selectedApp, setSelectedApp] = useState(null);
    const [loading, setLoading] = useState(false);
    const [granting, setGranting] = useState(false);

    const { call: getCompanyFunctions } = useBluelagoon({ method: "getCompanyFunctions" });
    const { call: createFunctionGrant } = useBluelagoon({ method: "createFunctionGrant" });
    const { call: setupApiAccess } = useBluelagoon({ method: "setupSharedFunctionApiAccess" });

    useEffect(() => {
        if (!open) { setSelectedApp(null); setSearchTerm(""); return; }
        setLoading(true);
        getCompanyFunctions({ search: debouncedSearch })
            .then((res) => { if (res?.data) setApps(res.data); })
            .finally(() => setLoading(false));
    }, [open, debouncedSearch, getCompanyFunctions]);

    const handleSelectFunction = async (fn) => {
        if (!workflowId || !selectedApp) return;
        setGranting(true);
        try {
            if (workflowType === "APP_V2") {
                const res = await createFunctionGrant({
                    componentId: fn.id,
                    sourceWorkflowId: selectedApp.id,
                    grantedWorkflowId: workflowId,
                });
                if (res?.success === false) {
                    if (res.status !== 409) {
                        toast.error(res.error || t("appPreview.functionShareFailed"));
                        setGranting(false);
                        return;
                    }
                }
                onSelect({ ...fn, appName: selectedApp.name, appId: selectedApp.id, wrapperName: buildSharedFunctionName(selectedApp.name, { filePath: fn.filePath, name: fn.name }) });
            } else {
                const res = await setupApiAccess({
                    componentId: fn.id,
                    sourceWorkflowId: selectedApp.id,
                    targetWorkflowId: workflowId,
                });
                if (!res?.success) {
                    toast.error(res?.error || t("appPreview.functionShareFailed"));
                    setGranting(false);
                    return;
                }
                onSelect({ ...fn, appName: selectedApp.name, appId: selectedApp.id, apiAccessData: res.data });
            }
        } catch (err) {
            if (err?.status === 409) {
                onSelect({ ...fn, appName: selectedApp.name, appId: selectedApp.id, wrapperName: buildSharedFunctionName(selectedApp.name, { filePath: fn.filePath, name: fn.name }) });
            } else {
                toast.error(err?.error || t("appPreview.functionShareFailed"));
            }
        } finally {
            setGranting(false);
        }
    };

    const functions = selectedApp?.functions?.filter((fn) =>
        !searchTerm || fn.name.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    return (
        <Dialog open={open} onOpenChange={granting ? undefined : onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {selectedApp && !granting && (
                            <button onClick={() => { setSelectedApp(null); setSearchTerm(""); }} className="hover:bg-gray-100 rounded p-1">
                                <ChevronLeft className="h-4 w-4" />
                            </button>
                        )}
                        {selectedApp ? selectedApp.name : t("appPreview.sharedFunctions")}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    {!granting && (
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                                placeholder={selectedApp ? t("appPreview.searchFunctions") : t("appPreview.searchApps")}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                                autoFocus
                            />
                        </div>
                    )}
                    <div className="max-h-[300px] overflow-y-auto space-y-1">
                        {loading || granting ? (
                            <div className="flex flex-col items-center py-8 gap-2">
                                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                                {granting && <p className="text-sm text-gray-500">{t("appPreview.sharingFunction")}</p>}
                            </div>
                        ) : !selectedApp ? (
                            <AppList apps={apps.filter((app) => app.id !== workflowId)} search={searchTerm} onSelect={(app) => { setSelectedApp(app); setSearchTerm(""); }} t={t} />
                        ) : (
                            <FunctionList functions={functions} onSelect={handleSelectFunction} t={t} />
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function AppList({ apps, search, onSelect, t }) {
    const filtered = apps.filter((app) =>
        !search || app.name.toLowerCase().includes(search.toLowerCase())
    );
    if (filtered.length === 0) {
        return <p className="text-center py-8 text-gray-500 text-sm">{t("appPreview.noAppsWithFunctions")}</p>;
    }
    return filtered.map((app) => (
        <button
            key={app.id}
            className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 transition-colors text-left"
            onClick={() => onSelect(app)}
        >
            <div className="p-2 bg-blue-50 rounded-md">
                <AppWindow className="h-4 w-4 text-blue-500" />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{app.name}</p>
                <p className="text-xs text-gray-500">{t("appPreview.functionCount", { count: app.functions.length })}</p>
            </div>
        </button>
    ));
}

function FunctionList({ functions, onSelect, t }) {
    if (functions.length === 0) {
        return <p className="text-center py-8 text-gray-500 text-sm">{t("appPreview.noFunctionsFound")}</p>;
    }
    return functions.map((fn) => (
        <button
            key={fn.id}
            className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 transition-colors text-left"
            onClick={() => onSelect(fn)}
        >
            <div className="p-2 bg-purple-50 rounded-md">
                <Code className="h-4 w-4 text-purple-500" />
            </div>
            <div className="flex-1 overflow-hidden">
                <p className="text-sm font-medium text-gray-900 truncate">{fn.name}</p>
                {fn.description && <p className="text-xs text-gray-500 line-clamp-2">{fn.description}</p>}
                <p className="text-xs text-gray-400 truncate">{fn.filePath}</p>
            </div>
        </button>
    ));
}
